import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory from './media-enterprise-public-media-v1-enterprise-category-enterprise-media-categories.http.js';

/** A category used to organize enterprise media items. Categories are organized in a two-level hierarchy: a root account category (created during onboarding) and categories nested within it. */
export interface EnterpriseCategory {
  /**
   * Category ID.
   * @maxLength 100
   * @readonly
   */
  _id?: string;
  /**
   * Display name shown for the category.
   * @maxLength 100
   * @minLength 2
   */
  displayName?: string | null;
  /**
   * ID of the parent category.
   *
   * Default: Account category ID.
   * @maxLength 100
   */
  parentCategoryId?: string | null;
  /** Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering. */
  sortOrder?: number | null;
  /** Publication status of the category. Controls whether the category is visible to child accounts. */
  publishStatus?: PublishStatusWithLiterals;
  /**
   * Date and time the category was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the category was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export enum PublishStatus {
  /** Unknown publish status. */
  UNDEFINED = 'UNDEFINED',
  /** Not published. Visible only in the account dashboard. */
  UNPUBLISHED = 'UNPUBLISHED',
  /** Published. Visible in child accounts' media managers. */
  PUBLISHED = 'PUBLISHED',
}

/** @enumType */
export type PublishStatusWithLiterals =
  | PublishStatus
  | 'UNDEFINED'
  | 'UNPUBLISHED'
  | 'PUBLISHED';

export enum MediaType {
  /** Mixed media types. Used for categories that contain multiple types of media. Individual items should use specific media types (IMAGE, VIDEO, etc.) rather than MIXED. */
  MIXED = 'MIXED',
  /** Image file. */
  IMAGE = 'IMAGE',
  /** Video file. */
  VIDEO = 'VIDEO',
  /** Audio file. */
  AUDIO = 'AUDIO',
  /** Document file. */
  DOCUMENT = 'DOCUMENT',
  /** Vector image file. */
  VECTOR = 'VECTOR',
  /** Archive file. Supported formats: ZIP, RAR, TAR, TAR.GZ, GZ, GZIP, JAR, 7Z, FGZ, and WEBARCHIVE. Maximum file size: 4000MB. */
  ARCHIVE = 'ARCHIVE',
  /** 3D model file. */
  MODEL3D = 'MODEL3D',
}

/** @enumType */
export type MediaTypeWithLiterals =
  | MediaType
  | 'MIXED'
  | 'IMAGE'
  | 'VIDEO'
  | 'AUDIO'
  | 'DOCUMENT'
  | 'VECTOR'
  | 'ARCHIVE'
  | 'MODEL3D';

export interface CreateCategoryRequest {
  /** Category to create. */
  category: EnterpriseCategory;
}

export interface CreateCategoryResponse {
  /** Created category. */
  category?: EnterpriseCategory;
}

export interface DeleteCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId: string;
}

export interface DeleteCategoryResponse {}

export interface UpdateCategoryRequest {
  /** Category to update. */
  category: EnterpriseCategory;
}

export interface UpdateCategoryResponse {
  /** Updated category. */
  category?: EnterpriseCategory;
}

export interface GetCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId: string;
  /** Number of subcategory levels to include in the response. For example, `levels=1` returns the category with its immediate children, `levels=2` includes grandchildren as well. When not specified, returns only the requested category without subcategories. */
  levels?: number | null;
  /** Publish status to filter categories by. Returns both `PUBLISHED` and `UNPUBLISHED` categories by default. When specified in addition to `levels`, this filter applies to all categories in the response, not just the root. */
  publishStatus?: PublishStatusWithLiterals;
}

export interface GetCategoryResponse {
  /** Category details including subcategories if requested. */
  category?: EnterpriseCategoryTree;
}

/** A category and its nested categories, as returned by [Get Category](#getcategory). */
export interface EnterpriseCategoryTree {
  /** Category information. */
  category?: EnterpriseCategory;
  /**
   * Nested categories under this category. Only populated when retrieving an account-level category; nested categories don't have further sub-categories.
   * @maxSize 100
   */
  subCategories?: EnterpriseCategoryTree[];
}

export interface EnterpriseOnboardingRequest {
  /**
   * Account ID of the organization. Used as the organization category ID.
   * @format GUID
   */
  accountId: string;
  /**
   * Account name of the organization. Used as the organization category name.
   * @maxLength 50
   */
  accountName?: string;
}

export interface EnterpriseOnboardingResponse {
  /** Created enterprise category. */
  category?: EnterpriseCategory;
}

export interface LinkItemsToCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId?: string;
  /**
   * IDs of items to link to the category.
   * @maxLength 100
   * @maxSize 50
   */
  itemIds?: string[];
}

export interface LinkItemsToCategoryResponse {}

export interface UnlinkItemsFromCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId?: string;
  /**
   * IDs of items to unlink from the category.
   * @maxLength 100
   * @maxSize 50
   */
  itemIds?: string[];
}

export interface UnlinkItemsFromCategoryResponse {}

export interface GetMediaManagerCategoriesRequest {}

export interface GetMediaManagerCategoriesResponse {
  /** Category tree for the media manager. */
  category?: EnterpriseCategoryTree;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface EnterpriseCategoryCreatedEnvelope {
  entity: EnterpriseCategory;
  metadata: EventMetadata;
}

/**
 * Triggered when a category is created.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_category_created
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService
 * @slug created
 */
export declare function onEnterpriseCategoryCreated(
  handler: (event: EnterpriseCategoryCreatedEnvelope) => void | Promise<void>
): void;

export interface EnterpriseCategoryDeletedEnvelope {
  entity: EnterpriseCategory;
  metadata: EventMetadata;
}

/**
 * Triggered when a category is deleted.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_category_deleted
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService
 * @slug deleted
 */
export declare function onEnterpriseCategoryDeleted(
  handler: (event: EnterpriseCategoryDeletedEnvelope) => void | Promise<void>
): void;

export interface EnterpriseCategoryUpdatedEnvelope {
  entity: EnterpriseCategory;
  metadata: EventMetadata;
}

/**
 * Triggered when a category is updated.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_category_updated
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService
 * @slug updated
 */
export declare function onEnterpriseCategoryUpdated(
  handler: (event: EnterpriseCategoryUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a category under the account's master category. Only one level of categories is supported under the master category. Before creating categories, ensure [Enterprise Onboarding](#enterpriseonboarding) has been called to initialize the account's root category structure.
 *
 * To delete a category, call [Delete Category](#deletecategory).
 * @param category - Category to create.
 * @public
 * @requiredField category
 * @requiredField category.displayName
 * @permissionId MEDIA.ENTERPRISE_MEDIA_CATEGORIES_CREATE
 * @applicableIdentity APP
 * @returns Created category.
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.CreateCategory
 */
export async function createCategory(
  category: NonNullablePaths<EnterpriseCategory, `displayName`, 2>
): Promise<NonNullablePaths<EnterpriseCategory, `_id` | `publishStatus`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ category: category });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory.createCategory(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { category: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['category']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes a category including all its subcategories. Items in the category are not deleted.
 *
 * To create a category, call [Create Category](#createcategory).
 * @param categoryId - Category ID.
 * @public
 * @requiredField categoryId
 * @permissionId MEDIA.ENTERPRISE_MEDIA_CATEGORIES_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.DeleteCategory
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory.deleteCategory(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { categoryId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['categoryId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a category.
 * @param _id - Category ID.
 * @public
 * @requiredField _id
 * @requiredField category
 * @permissionId MEDIA.ENTERPRISE_MEDIA_CATEGORIES_UPDATE
 * @applicableIdentity APP
 * @returns Updated category.
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.UpdateCategory
 */
export async function updateCategory(
  _id: string,
  category: UpdateCategory
): Promise<NonNullablePaths<EnterpriseCategory, `_id` | `publishStatus`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    category: { ...category, id: _id },
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory.updateCategory(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { category: '$[1]' },
        explicitPathsToArguments: { 'category.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'category']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCategory {
  /**
   * Category ID.
   * @maxLength 100
   * @readonly
   */
  _id?: string;
  /**
   * Display name shown for the category.
   * @maxLength 100
   * @minLength 2
   */
  displayName?: string | null;
  /**
   * ID of the parent category.
   *
   * Default: Account category ID.
   * @maxLength 100
   */
  parentCategoryId?: string | null;
  /** Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering. */
  sortOrder?: number | null;
  /** Publication status of the category. Controls whether the category is visible to child accounts. */
  publishStatus?: PublishStatusWithLiterals;
  /**
   * Date and time the category was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the category was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/**
 * Retrieves a category by ID, optionally including subcategories.
 * @param categoryId - Category ID.
 * @public
 * @requiredField categoryId
 * @permissionId MEDIA.ENTERPRISE_MEDIA_CATEGORIES_READ
 * @applicableIdentity APP
 * @returns Category details including subcategories if requested.
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.GetCategory
 */
export async function getCategory(
  categoryId: string,
  options?: GetCategoryOptions
): Promise<
  NonNullablePaths<
    EnterpriseCategoryTree,
    `category._id` | `category.publishStatus` | `subCategories`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
    levels: options?.levels,
    publishStatus: options?.publishStatus,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory.getCategory(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          categoryId: '$[0]',
          levels: '$[1].levels',
          publishStatus: '$[1].publishStatus',
        },
        singleArgumentUnchanged: false,
      },
      ['categoryId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetCategoryOptions {
  /** Number of subcategory levels to include in the response. For example, `levels=1` returns the category with its immediate children, `levels=2` includes grandchildren as well. When not specified, returns only the requested category without subcategories. */
  levels?: number | null;
  /** Publish status to filter categories by. Returns both `PUBLISHED` and `UNPUBLISHED` categories by default. When specified in addition to `levels`, this filter applies to all categories in the response, not just the root. */
  publishStatus?: PublishStatusWithLiterals;
}

/**
 * Initializes the enterprise category structure for an organization during first-time setup. Creates the root account category using the provided account information.
 * If the root account category already exists, this method returns the existing category structure without creating duplicates.
 *
 * The caller's account ID must match the `accountId` in the request.
 * @param accountId - Account ID of the organization. Used as the organization category ID.
 * @public
 * @requiredField accountId
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.EnterpriseOnboarding
 */
export async function enterpriseOnboarding(
  accountId: string,
  options?: EnterpriseOnboardingOptions
): Promise<
  NonNullablePaths<
    EnterpriseOnboardingResponse,
    `category._id` | `category.publishStatus`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    accountId: accountId,
    accountName: options?.accountName,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory.enterpriseOnboarding(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          accountId: '$[0]',
          accountName: '$[1].accountName',
        },
        singleArgumentUnchanged: false,
      },
      ['accountId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EnterpriseOnboardingOptions {
  /**
   * Account name of the organization. Used as the organization category name.
   * @maxLength 50
   */
  accountName?: string;
}

/**
 * Retrieves the category tree for the media manager, including all categories accessible to the current user.
 * @public
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.GetMediaManagerCategories
 */
export async function getMediaManagerCategories(): Promise<
  NonNullablePaths<
    GetMediaManagerCategoriesResponse,
    | `category.category._id`
    | `category.category.publishStatus`
    | `category.subCategories`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseCategory.getMediaManagerCategories(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
